import React, { useState, ChangeEvent, FormEvent, useEffect } from "react";
import {
  Country,
  deleteUser,
  GetCountryDto,
  searchCountry,
} from "../../services/api/dietplan";

interface AccountDeletionPageProps {
  endpoint?: string;
  supportEmail?: string;
  appName?: string;
  updatedAt?: Date;
}

interface FormState {
  email: string;
  phone: string;
  userId: string;
  country: string;
  reason: string;
  confirmPermanent: boolean;
  confirmEmail: boolean;
}

interface StatusState {
  ok: boolean;
  error: string;
}

interface DeletionPayload {
  email: string;
  phone: string;
  userId: string;
  country: string;
  reason: string;
  confirmEmail: boolean;
  source: string;
  ts: string;
}

interface StepProps {
  num: number;
  title: string;
  pill: string;
  variant?: "warning";
  children: React.ReactNode;
}

export default function AccountDeletionPage({
  endpoint = "/api/delete-account",
  supportEmail = "rcpatel808@gmail.com",
  appName = "AI Diet Plan",
  updatedAt = new Date(),
}: AccountDeletionPageProps) {
  const [form, setForm] = useState<FormState>({
    email: "",
    phone: "",
    userId: "",
    country: "",
    reason: "",
    confirmPermanent: false,
    confirmEmail: false,
  });

  const [status, setStatus] = useState<StatusState>({ ok: false, error: "" });
  const [listOfCountry, setListOfCountry] = useState<Country[]>([]);

  useEffect(() => {
    getCountryList();
  }, []);
  const getCountryList = async () => {
    let countries = await searchCountry();
    setListOfCountry(countries.data);
  };
  const dateStr = new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "long",
    day: "2-digit",
  }).format(updatedAt);

  const onChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type, checked } = target;
    setForm((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
  };

  const emailFallback = () => {
    const params = new URLSearchParams({
      subject: `${appName} - Account Deletion Request`,
      body: `Email: ${form.email || ""}\nPhone: ${form.phone || ""}\nUser ID: ${
        form.userId || ""
      }\nReason: ${form.reason || ""}`,
    });
    window.location.href = `mailto:${supportEmail}?${params.toString()}`;
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus({ ok: false, error: "" });

    if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email)) {
      setStatus({ ok: false, error: "Please enter a valid email address." });
      return;
    }
    if (!form.confirmPermanent) {
      setStatus({
        ok: false,
        error: "Please confirm that you understand deletion is permanent.",
      });
      return;
    }

    // const payload: DeletionPayload = {
    //   email: form.email.trim(),
    //   phone: form.phone.trim(),
    //   userId: form.userId.trim(),
    //   country: form.country,
    //   reason: form.reason.trim(),
    //   confirmEmail: form.confirmEmail,
    //   source: "public-deletion-page",
    //   ts: new Date().toISOString(),
    // };

    // try {
    //   const res = await fetch(endpoint, {
    //     method: "POST",
    //     headers: { "Content-Type": "application/json" },
    //     body: JSON.stringify(payload),
    //   });
    //   if (!res.ok) throw new Error("Network response was not ok");
    //   setStatus({ ok: true, error: "" });
    //   setForm({
    //     email: "",
    //     phone: "",
    //     userId: "",
    //     country: "",
    //     reason: "",
    //     confirmPermanent: false,
    //     confirmEmail: false,
    //   });
    // } catch (err) {
    //   console.error(err);
    //   setStatus({
    //     ok: false,
    //     error:
    //       "Something went wrong. Please try again or use the email option.",
    //   });
    // }
  };

  const handleDeletion = async () => {
    const res = await deleteUser(form.email);
    console.log({res});
    if(res.status === 200) {
      setStatus({ ok: true, error: "" });
    } else {
      setStatus({ ok: false, error: res.response?.data?.message || "Something went wrong with this user" });
    }
  };
  return (
    <div
      className="min-h-screen text-slate-50"
      style={{
        background: `radial-gradient(1200px 600px at 20% -20%, rgba(34,211,238,.18), transparent 60%),
                   radial-gradient(900px 500px at 120% 20%, rgba(52,211,153,.16), transparent 60%),
                   linear-gradient(180deg, #0b1020 0%, #0a0f1a 100%)`,
        backgroundAttachment: "fixed",
      }}
    >
      <style>{`
        :root {
          --card: rgba(255,255,255,0.06);
          --card-strong: rgba(255,255,255,0.12);
          --muted: #cbd5e1;
          --brand: #22d3ee;
          --accent: #34d399;
        }
        .glass { 
          background: var(--card); 
          backdrop-filter: blur(10px); 
          box-shadow: 0 10px 30px rgba(0,0,0,.35), inset 0 0 0 1px rgba(255,255,255,.06); 
        }
        .divider { 
          height: 1px; 
          background: linear-gradient(90deg, transparent, rgba(255,255,255,.12), transparent); 
        }
        .badge { 
          font-size: 12px; 
          color: var(--muted); 
          border: 1px solid rgba(255,255,255,.12); 
          border-radius: 999px; 
          padding: 6px 10px; 
        }
        .pill { 
          display: inline-flex; 
          align-items: center; 
          gap: 8px; 
          padding: 6px 10px; 
          border-radius: 999px; 
          background: rgba(16,185,129,.15); 
          border: 1px solid rgba(16,185,129,.35); 
          color: #d1fae5; 
          font-size: 12px; 
        }
        .pill-warning { 
          background: rgba(245,158,11,.12); 
          border-color: rgba(245,158,11,.35); 
          color: #fde68a; 
        }
        .pill-danger { 
          background: rgba(239,68,68,.12); 
          border-color: rgba(239,68,68,.35); 
          color: #fecaca; 
        }
        .input { 
          width: 100%; 
          padding: 12px 14px; 
          border-radius: 10px; 
          border: 1px solid rgba(255,255,255,.12); 
          background: rgba(10,12,20,.6); 
          color: #f8fafc; 
          outline: none;
          transition: border .2s, box-shadow .2s, background .2s;
        }
        .input::placeholder { 
          color: #9aa3b2; 
        }
        .input:focus { 
          border-color: var(--brand); 
          box-shadow: 0 0 0 4px rgba(34,211,238,.15); 
          background: rgba(10,12,20,.8); 
        }
        .help { 
          font-size: 12px; 
          color: var(--muted); 
        }
        .btn-primary { 
          background: linear-gradient(135deg, var(--brand), var(--accent)); 
          color: #001016; 
          font-weight: 700;
          border: none;
          border-radius: 12px;
          padding: 12px 16px;
          cursor: pointer;
          box-shadow: 0 8px 20px rgba(34,211,238,.25), inset 0 0 0 1px rgba(255,255,255,.18);
        }
        .btn-secondary {
          background: transparent;
          border: 1px solid rgba(255,255,255,.18);
          color: #f8fafc;
          border-radius: 12px;
          padding: 12px 16px;
          cursor: pointer;
        }
        .checkbox-custom {
          width: 18px;
          height: 18px;
          margin-top: 2px;
          accent-color: var(--accent);
        }
      `}</style>

      <div
        style={{ maxWidth: "1100px", margin: "0 auto", padding: "28px" }}
        className="text-white"
      >
        {/* Header */}
        <header
          style={{
            display: "grid",
            gap: "14px",
            justifyItems: "start",
            margin: "24px 0 28px",
          }}
        >
          <span
            className="glass"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "12px",
              padding: "10px 14px",
              borderRadius: "999px",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              style={{ width: "28px", height: "28px", color: "#34d399" }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M12 3c2 2 3 4.5 3 7.5S14 16 12 18c-2-2-3-4.5-3-7.5S10 5 12 3z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M5 21c3-2 7-2 10 0"
              />
            </svg>
            <strong>{appName}</strong>
            <span className="badge">Account & Data Deletion</span>
          </span>
          <h1
            style={{
              fontSize: "clamp(28px, 5vw, 46px)",
              lineHeight: "1.05",
              margin: "6px 0 8px",
              color: "#fff",
            }}
          >
            Delete your account and personal data
          </h1>
          <p style={{ color: "var(--muted)", maxWidth: "66ch" }}>
            This page lets you request deletion of your {appName} account{" "}
            <strong>without signing in</strong> in line with Google Play's User
            Data policy. Once completed, your account and personal data will be
            deleted within <strong>7 days</strong> (and scrubbed from backups
            within <strong>30 days</strong>).
          </p>
        </header>

        {/* Main grid */}
        <div
          style={{
            display: "grid",
            gap: "22px",
            gridTemplateColumns: window.innerWidth > 900 ? "1.2fr .9fr" : "1fr",
          }}
        >
          {/* Form card */}
          <section
            className="glass"
            style={{ borderRadius: "22px", padding: "22px" }}
            aria-labelledby="request-form"
          >
            <h2 id="request-form" style={{ margin: 0 }}>
              <strong>Submit a deletion request</strong>
            </h2>
            <div className="divider" style={{ margin: "16px 0" }} />

            <form onSubmit={onSubmit} noValidate>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    window.innerWidth > 700 ? "1fr 1fr" : "1fr",
                  gap: "14px",
                }}
              >
                <div>
                  <label
                    htmlFor="email"
                    style={{
                      fontSize: "14px",
                      color: "var(--muted)",
                      display: "block",
                      margin: "2px 0 6px",
                    }}
                  >
                    Email address used in the app *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="input"
                    value={form.email}
                    onChange={onChange}
                  />
                  <p className="help" style={{ marginTop: "6px" }}>
                    We'll verify your request via this email.
                  </p>
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    style={{
                      fontSize: "14px",
                      color: "var(--muted)",
                      display: "block",
                      margin: "2px 0 6px",
                    }}
                  >
                    Registered phone (optional)
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+91 98765 43210"
                    pattern="^[0-9+()\\s-]{7,}$"
                    className="input"
                    value={form.phone}
                    onChange={onChange}
                  />
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    window.innerWidth > 700 ? "1fr 1fr" : "1fr",
                  gap: "14px",
                  marginTop: "14px",
                }}
              >
                <div>
                  <label
                    htmlFor="userId"
                    style={{
                      fontSize: "14px",
                      color: "var(--muted)",
                      display: "block",
                      margin: "2px 0 6px",
                    }}
                  >
                    User ID (optional)
                  </label>
                  <input
                    id="userId"
                    name="userId"
                    type="text"
                    placeholder="e.g., AIDP-12345"
                    className="input"
                    value={form.userId}
                    onChange={onChange}
                  />
                  <p className="help" style={{ marginTop: "6px" }}>
                    Find this in the app under Settings → Profile.
                  </p>
                </div>
                {/* <div>
                  <label htmlFor="country" style={{ fontSize: '14px', color: 'var(--muted)', display: 'block', margin: '2px 0 6px' }}>
                    Country/Region
                  </label>
                  <select id="country" name="country" className="input" value={form.country} onChange={onChange}>
                    <option value="">Choose...</option>
                    <option>India</option>
                    <option>United States</option>
                    <option>United Kingdom</option>
                    <option>Canada</option>
                    <option>Australia</option>
                    <option>European Union</option>
                    <option>Other</option>
                  </select>
                </div> */}
                <div>
                  <label
                    htmlFor="country"
                    style={{
                      fontSize: "14px",
                      color: "var(--muted)",
                      display: "block",
                      margin: "2px 0 6px",
                    }}
                  >
                    Country/Region
                  </label>

                  <select
                    id="country"
                    name="country"
                    className="input"
                    value={form.country}
                    onChange={onChange}
                  >
                    <option value="">Choose...</option>
                    {listOfCountry.map((item) => (
                      <option key={item.id} value={item.name}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ marginTop: "14px" }}>
                <label
                  htmlFor="reason"
                  style={{
                    fontSize: "14px",
                    color: "var(--muted)",
                    display: "block",
                    margin: "2px 0 6px",
                  }}
                >
                  Reason for deletion (optional)
                </label>
                <textarea
                  id="reason"
                  name="reason"
                  rows={3}
                  placeholder="Tell us anything we should know (optional)"
                  className="input"
                  value={form.reason}
                  onChange={onChange}
                />
              </div>

              <div style={{ display: "grid", gap: "10px", marginTop: "10px" }}>
                <label
                  style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "start",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    name="confirmPermanent"
                    checked={form.confirmPermanent}
                    onChange={onChange}
                    className="checkbox-custom"
                  />
                  <span>
                    I understand this action is <strong>permanent</strong> and
                    will delete my account and associated personal data.
                  </span>
                </label>
                <label
                  style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "start",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    name="confirmEmail"
                    checked={form.confirmEmail}
                    onChange={onChange}
                    className="checkbox-custom"
                  />
                  <span>
                    Send me a confirmation email when deletion is complete.
                  </span>
                </label>
              </div>

              <div className="divider" style={{ margin: "16px 0" }} />

              <div
                style={{
                  background: "rgba(255,255,255,.04)",
                  border: "1px dashed rgba(255,255,255,.18)",
                  borderRadius: "10px",
                  padding: "14px",
                  color: "var(--muted)",
                  fontSize: "13px",
                }}
                role="status"
                aria-live="polite"
              >
                <strong>Verification:</strong> We use rate-limiting and
                anti-abuse checks. If you run into issues, email{" "}
                <a style={{ color: "#7dd3fc" }} href={`mailto:${supportEmail}`}>
                  {supportEmail}
                </a>
                .
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  flexWrap: "wrap",
                  marginTop: "14px",
                }}
              >
                <button
                  type="submit"
                  className="btn-primary"
                  onClick={handleDeletion}
                >
                  Request deletion
                </button>
                <button
                  type="button"
                  onClick={emailFallback}
                  className="btn-secondary"
                >
                  Email my request
                </button>
              </div>

              {status.ok && (
                <div
                  style={{
                    marginTop: "12px",
                    padding: "12px 14px",
                    borderRadius: "12px",
                    background: "rgba(16,185,129,.1)",
                    border: "1px solid rgba(16,185,129,.3)",
                  }}
                  role="alert"
                >
                  User account with email {form.email} deleted successfully
                </div>
              )}
              {!!status.error && (
                <div
                  style={{
                    marginTop: "12px",
                    padding: "12px 14px",
                    borderRadius: "12px",
                    background: "rgba(239,68,68,.1)",
                    border: "1px solid rgba(239,68,68,.3)",
                  }}
                  role="alert"
                >
                  {status.error}
                </div>
              )}

              <p
                style={{
                  fontSize: "13px",
                  color: "var(--muted)",
                  marginTop: "12px",
                }}
              >
                By submitting, you request deletion under our{" "}
                <a href="#policy" style={{ color: "#7dd3fc" }}>
                  Deletion Policy
                </a>
                . You can also request a{" "}
                <a href="#export" style={{ color: "#7dd3fc" }}>
                  data export
                </a>{" "}
                before deletion.
              </p>
            </form>
          </section>

          {/* Policy sidebar */}
          <aside
            className="glass"
            style={{ borderRadius: "22px", padding: "22px" }}
            aria-labelledby="policy"
          >
            <h2 id="policy">
              <strong>How deletion works</strong>
            </h2>
            <div style={{ display: "grid", gap: "16px", marginTop: "16px" }}>
              <Step num={1} title="Verify ownership" pill="No login required">
                We may email you to confirm you own the account and to prevent
                unauthorized deletion.
              </Step>
              <Step
                num={2}
                title="Deactivate immediately"
                pill="Up to 24 hours"
                variant="warning"
              >
                We deactivate access right away while we process deletion.
              </Step>
              <Step num={3} title="Delete data" pill="Backups & logs minimized">
                Personal data is deleted within <strong>7 days</strong> and
                removed from backups within <strong>30 days</strong>.
              </Step>
              <Step num={4} title="Confirmation" pill="Proof of deletion">
                If selected, we'll email a confirmation when deletion is
                complete.
              </Step>
            </div>

            <div className="divider" style={{ margin: "16px 0" }} />

            <h3>
              <strong>Data we delete</strong>
            </h3>
            <ul style={{ marginTop: "8px", paddingLeft: "20px" }}>
              <li>Account profile (name, email, phone)</li>
              <li>Diet preferences, goals, and AI-generated plans</li>
              <li>Usage metrics linked to your account</li>
              <li>In-app purchases tied to your account (non-refundable)</li>
            </ul>

            <h3 style={{ marginTop: "16px" }}>
              <strong>What we may retain</strong>
            </h3>
            <p style={{ color: "var(--muted)" }}>
              We retain only what's necessary to comply with law, resolve
              disputes, and prevent abuse, such as payment records and minimal
              audit logs.
            </p>
            <span className="pill pill-danger" style={{ marginTop: "8px" }}>
              Minimal & time-limited
            </span>

            <div className="divider" style={{ margin: "16px 0" }} />

            <h3 id="export">
              <strong>Request your data export</strong>
            </h3>
            <p style={{ color: "var(--muted)" }}>
              Before deletion, you can request a copy of your data. Email{" "}
              <a
                href={`mailto:${supportEmail}?subject=${encodeURIComponent(
                  appName + " - Data Export Request"
                )}`}
                style={{ color: "#7dd3fc" }}
              >
                {supportEmail}
              </a>{" "}
              with the subject{" "}
              <code
                style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: "12px",
                  color: "#bae6fd",
                }}
              >
                Data Export Request
              </code>
              .
            </p>

            <div className="divider" style={{ margin: "16px 0" }} />

            <div style={{ display: "grid", gap: "8px" }}>
              <details
                className="glass"
                style={{
                  borderRadius: "14px",
                  padding: "14px 16px",
                  border: "1px solid rgba(255,255,255,.08)",
                }}
              >
                <summary style={{ cursor: "pointer", fontWeight: "600" }}>
                  Do I have to sign in?
                </summary>
                <p style={{ color: "var(--muted)", marginTop: "8px" }}>
                  No. This page is publicly accessible to comply with Google
                  Play policy.
                </p>
              </details>
              <details
                className="glass"
                style={{
                  borderRadius: "14px",
                  padding: "14px 16px",
                  border: "1px solid rgba(255,255,255,.08)",
                }}
              >
                <summary style={{ cursor: "pointer", fontWeight: "600" }}>
                  Can I undo deletion?
                </summary>
                <p style={{ color: "var(--muted)", marginTop: "8px" }}>
                  No. Deletion is permanent once processed.
                </p>
              </details>
              <details
                className="glass"
                style={{
                  borderRadius: "14px",
                  padding: "14px 16px",
                  border: "1px solid rgba(255,255,255,.08)",
                }}
              >
                <summary style={{ cursor: "pointer", fontWeight: "600" }}>
                  How do I contact support?
                </summary>
                <p style={{ color: "var(--muted)", marginTop: "8px" }}>
                  Email{" "}
                  <a
                    href={`mailto:${supportEmail}`}
                    style={{ color: "#7dd3fc" }}
                  >
                    {supportEmail}
                  </a>
                  . We typically respond within 2 business days.
                </p>
              </details>
            </div>
          </aside>
        </div>

        <footer style={{ margin: "30px 0 12px" }}>
          <div className="divider" style={{ margin: "16px 0" }} />
          <p style={{ color: "var(--muted)", fontSize: "13px" }}>
            Public page — no login required · Last updated: {dateStr} · ©{" "}
            {appName}
          </p>
        </footer>
      </div>
    </div>
  );
}

function Step({ num, title, pill, variant, children }: StepProps) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "36px 1fr",
        gap: "12px",
        alignItems: "start",
      }}
    >
      <div
        style={{
          width: "36px",
          height: "36px",
          borderRadius: "50%",
          background: "var(--card-strong)",
          display: "grid",
          placeItems: "center",
        }}
      >
        {num}
      </div>
      <div>
        <h4 style={{ margin: "2px 0 4px" }}>{title}</h4>
        <p style={{ color: "var(--muted)" }}>{children}</p>
        <span
          className={`pill ${variant === "warning" ? "pill-warning" : ""}`}
          style={{ marginTop: "4px" }}
        >
          {pill}
        </span>
      </div>
    </div>
  );
}
